import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Sparkles, LogOut, GraduationCap, Award, IndianRupee, Megaphone, BookOpen, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) { navigate('/student/login'); return; }
    axios.get(`${API}/student/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setData(res.data.data); setLoading(false); })
      .catch(() => {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('student_token');
        localStorage.removeItem('student_user');
        navigate('/student/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_user');
    navigate('/student/login');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-slate-400">Loading dashboard...</p>
    </div>
  );

  if (!data) return null;

  const { student, exams, invoices, fee_summary, school, announcements } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md shadow-teal-500/20">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800">Ariskool</span>
              <p className="text-[10px] text-slate-400">{school?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-slate-800" data-testid="student-name">{student.full_name}</p>
              <p className="text-[10px] text-teal-600 font-medium">{student.class_name} {student.section} | {student.admission_number}</p>
            </div>
            <Button variant="ghost" size="sm" className="text-rose-500 hover:text-rose-600 hover:bg-rose-50" onClick={handleLogout} data-testid="student-logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="student-welcome">Welcome, {student.full_name}</h1>
          <p className="text-sm text-slate-500 mt-1">{student.class_name} {student.section} | Roll: {student.roll_number || 'N/A'} | {student.admission_number}</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center"><Award className="h-5 w-5 text-blue-500" /></div>
              <div><p className="text-xs text-slate-400">Exams</p><p className="text-xl font-bold text-slate-900">{exams.length}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-emerald-500" /></div>
              <div><p className="text-xs text-slate-400">Fee Paid</p><p className="text-xl font-bold text-emerald-600">{fee_summary.total_paid.toLocaleString()}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-amber-500" /></div>
              <div><p className="text-xs text-slate-400">Fee Balance</p><p className="text-xl font-bold text-amber-600">{fee_summary.balance.toLocaleString()}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center"><Megaphone className="h-5 w-5 text-violet-500" /></div>
              <div><p className="text-xs text-slate-400">Announcements</p><p className="text-xl font-bold text-slate-900">{announcements.length}</p></div>
            </div>
          </div>
        </div>

        {/* Exam Results */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="student-exam-results">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-teal-500" />
            <h2 className="text-lg font-bold text-slate-900">Exam Results</h2>
          </div>
          {exams.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <BookOpen className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>No exam results available yet</p>
            </div>
          ) : exams.map((exam, idx) => (
            <div key={idx} className="mb-6 last:mb-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">{exam.exam_name}</h3>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${exam.overall_percentage >= 75 ? 'bg-emerald-100 text-emerald-700' : exam.overall_percentage >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {exam.overall_percentage}%
                </span>
              </div>
              <div className="overflow-x-auto border border-slate-100 rounded-xl">
                <table className="w-full">
                  <thead><tr className="bg-slate-50">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Subject</th>
                    <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Max</th>
                    <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Obtained</th>
                    <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">%</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {exam.subjects.map((sub, si) => (
                      <tr key={si}>
                        <td className="px-4 py-2 text-sm text-slate-800">{sub.subject}</td>
                        <td className="px-4 py-2 text-sm text-slate-500 text-center">{sub.max_marks}</td>
                        <td className="px-4 py-2 text-sm font-semibold text-center">{sub.obtained}</td>
                        <td className="px-4 py-2 text-sm text-center">
                          <span className={`font-semibold ${sub.percentage >= 75 ? 'text-emerald-600' : sub.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{sub.percentage}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-slate-50 border-t">
                    <td className="px-4 py-2 text-sm font-bold">Total</td>
                    <td className="px-4 py-2 text-sm font-bold text-center">{exam.total_marks}</td>
                    <td className="px-4 py-2 text-sm font-bold text-center">{exam.total_obtained}</td>
                    <td className="px-4 py-2 text-sm font-bold text-center">{exam.overall_percentage}%</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          ))}
        </div>

        {/* Fee Status */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="student-fee-status">
          <div className="flex items-center gap-2 mb-4">
            <IndianRupee className="h-5 w-5 text-teal-500" />
            <h2 className="text-lg font-bold text-slate-900">Fee Status</h2>
          </div>
          {invoices.length === 0 ? (
            <p className="text-center py-8 text-slate-400">No fee records available</p>
          ) : (
            <div className="overflow-x-auto border border-slate-100 rounded-xl">
              <table className="w-full">
                <thead><tr className="bg-slate-50">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Month</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Amount</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Paid</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {invoices.map(inv => (
                    <tr key={inv.invoice_id}>
                      <td className="px-4 py-2 text-sm text-slate-800">{inv.month}</td>
                      <td className="px-4 py-2 text-sm text-right font-mono">{inv.net_amount?.toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm text-right font-mono text-emerald-600">{(inv.paid_amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          inv.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                          inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{inv.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="student-announcements">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone className="h-5 w-5 text-teal-500" />
              <h2 className="text-lg font-bold text-slate-900">School Announcements</h2>
            </div>
            <div className="space-y-3">
              {announcements.map(a => (
                <div key={a.announcement_id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="font-semibold text-sm text-slate-900">{a.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{a.message}</p>
                  <p className="text-[10px] text-slate-400 mt-2">{new Date(a.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
